import { Signal, computed } from '@angular/core';
import { ChallengeService } from './challenge.service';

export type PvpQueueLabel = 'starting' | 'exit' | 'challenging' | 'join';
export type PvpApprovalState = 'none' | 'needed' | 'pending';

const LABEL_TEXT: Record<PvpQueueLabel, string> = {
  starting: 'Starting...',
  challenging: 'Challenging…',
  exit: 'Exit Queue',
  join: 'PVP Queue',
};

export interface PvpQueueButtonDeps {
  challenge: ChallengeService;
  /** undefined while the team is still loading. */
  hasTeam: Signal<boolean | undefined>;
  /** Host unwinds its own optimistic UI; the queue itself is left via leaveSignedQueue. */
  cancelChallenge: () => void;
}

/**
 * The PvP queue CTA's state machine — label, disabled, approval state and the
 * activate branch — shared by desktop's battle-actions row and the mobile play
 * sheet so the two can't drift.
 */
export function createPvpQueueButton(deps: PvpQueueButtonDeps) {
  const { challenge, hasTeam, cancelChallenge } = deps;

  const label = computed<PvpQueueLabel>(() => {
    if (challenge.isMatched()) return 'starting';
    if (challenge.isChallenging()) return 'challenging';
    if (challenge.isWaiting()) return 'exit';
    return 'join';
  });

  /** Whether a spinner belongs next to the label. */
  const busy = computed(() => label() !== 'join');
  const text = computed(() => LABEL_TEXT[label()]);

  const approval = computed<PvpApprovalState>(() => {
    if (challenge.matchmakerApprovalPending()) return 'pending';
    return challenge.needsMatchmakerApproval() ? 'needed' : 'none';
  });

  const disabled = computed(
    () =>
      challenge.matchmakerApprovalPending() ||
      challenge.needsMatchmakerApproval() ||
      challenge.isMatched() ||
      challenge.isSigning() ||
      (!challenge.isInQueue() && !hasTeam()),
  );

  async function activate(): Promise<void> {
    // Cancel/rejoin must no-op once a match is committed.
    if (challenge.isMatched()) return;

    if (challenge.isChallenging()) {
      cancelChallenge();
      return;
    }

    if (challenge.isWaiting()) {
      challenge.leaveSignedQueue();
    } else {
      try {
        await challenge.joinSignedQueue();
      } catch (error) {
        console.error('Error joining signed queue:', error);
      }
    }
  }

  return { label, text, busy, approval, disabled, activate };
}
